import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Edit, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/useMobile";
import { AdminSidebar } from "@/components/AdminSidebar";
import { DEFAULT_WIDTH, SIDEBAR_WIDTH_KEY } from "@/components/AdminSidebar";
import { CSSProperties, useEffect } from "react";

export default function Templates() {
  const isMobile = useIsMobile();
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [previewTemplate, setPreviewTemplate] = useState<string>("");
  const [previewSubject, setPreviewSubject] = useState<string>("");
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return stored ? parseInt(stored) : DEFAULT_WIDTH;
  });

  const { data: user, isLoading: authLoading } = trpc.auth.me.useQuery();
  const { data: templates, isLoading, refetch } = trpc.templates.getAll.useQuery();
  const updateMutation = trpc.templates.update.useMutation();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  // Sample data for preview
  const sampleData = {
    participantName: "Max Mustermann",
    courseName: "Yoga für Anfänger",
    courseDate: "Montag, 15. Januar 2025",
    courseTime: "18:00 Uhr",
    dayOfWeek: "Montag",
    time: "18:00",
    courseLocation: "Physiotherapie Florian Übler",
    instructorName: "Florian Übler",
  };
  
  const renderPreview = (template: string) => {
    let rendered = template;
    Object.entries(sampleData).forEach(([key, value]) => {
      rendered = rendered.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    return rendered;
  };

  const hasAdminAccess = user?.roles?.includes("admin");

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">Laden...</div>
      </div>
    );
  }

  if (!hasAdminAccess) {
    window.location.href = "/";
    return null;
  }

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const data = {
      subject: formData.get("subject") as string || undefined,
      template: formData.get("template") as string,
      description: formData.get("description") as string || undefined,
    };

    try {
      await updateMutation.mutateAsync({ id: editingTemplate.id, ...data });
      toast.success("Vorlage aktualisiert");
      setIsDialogOpen(false);
      setEditingTemplate(null);
      setPreviewTemplate("");
      setPreviewSubject("");
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Fehler beim Speichern");
    }
  };

  const openEditDialog = (template: any) => {
    setEditingTemplate(template);
    setPreviewTemplate("");
    setPreviewSubject("");
    setIsDialogOpen(true);
  };

  const filteredTemplates = templates?.filter((t: any) => 
    channelFilter === "all" || t.channel === channelFilter
  ) || [];

  const getTypeLabel = (notificationType: string) => {
    const labels: Record<string, string> = {
      booking_confirmation_email: "Buchungsbestätigung",
      booking_confirmation_sms: "Buchungsbestätigung",
      booking_waitlist_email: "Wartelisten-Bestätigung",
      cancellation_email: "Kursabsage",
      cancellation_sms: "Kursabsage",
      course_reminder_email: "Kurserinnerung",
      reactivation_email: "Kurswiederaufnahme",
      reactivation_sms: "Kurswiederaufnahme",
      reminder_email: "Kurserinnerung",
      session_cancellation_email: "Kursabsage",
      session_reactivation_email: "Kurswiederaufnahme",
      user_invite_email: "Benutzereinladung",
      waitlist_promotion_email: "Wartelisten-Vorrutschen",
      waitlist_promotion_sms: "Wartelisten-Vorrutschen",
    };
    return labels[notificationType] || notificationType;
  };

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <AdminSidebar setSidebarWidth={setSidebarWidth} />
      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <span className="tracking-tight text-foreground font-medium">
                Vorlagen
              </span>
            </div>
          </div>
        )}
        <div className="container mx-auto py-4 sm:py-6 space-y-4 sm:space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Benachrichtigungsvorlagen</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Bearbeiten Sie Email- und SMS-Vorlagen
            </p>
          </div>

          {/* Tabs Filter */}
          <Tabs value={channelFilter} onValueChange={setChannelFilter} className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-3">
              <TabsTrigger value="all">Alle</TabsTrigger>
              <TabsTrigger value="email">Email</TabsTrigger>
              <TabsTrigger value="sms">SMS</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Templates List */}
          {isLoading ? (
            <div className="text-center py-8">Laden...</div>
          ) : !filteredTemplates.length ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Keine Vorlagen gefunden
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredTemplates.map((template: any) => (
                <Card key={template.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <CardTitle className="text-lg">{getTypeLabel(template.notificationType)}</CardTitle>
                          <Badge variant={template.channel === "sms" ? "default" : "secondary"}>
                            {template.channel === "sms" ? (
                              <><MessageSquare className="h-3 w-3 mr-1" />SMS</>
                            ) : (
                              <><Mail className="h-3 w-3 mr-1" />Email</>
                            )}
                          </Badge>
                          {!template.isActive && (
                            <Badge variant="outline">Inaktiv</Badge>
                          )}
                        </div>
                        {template.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {template.description}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(template)}
                        className="shrink-0"
                      >
                        <Edit className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Bearbeiten</span>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {template.subject && (
                      <div className="mb-2">
                        <span className="text-sm font-medium">Betreff: </span>
                        <span className="text-sm text-muted-foreground">{template.subject}</span>
                      </div>
                    )}
                    {template.channel === "email" && template.template.includes("<html") ? (
                      <div className="border rounded-md overflow-hidden">
                        <div className="bg-muted px-3 py-2 text-xs font-medium border-b">
                          HTML-Vorschau
                        </div>
                        <div className="bg-white p-4 max-h-96 overflow-y-auto">
                          <iframe
                            srcDoc={template.template}
                            className="w-full min-h-[300px] border-0"
                            sandbox="allow-same-origin"
                            title="Email Preview"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="bg-muted/50 rounded p-3 text-sm whitespace-pre-wrap max-h-96 overflow-y-auto">
                        {template.template}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Edit Dialog */}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Vorlage bearbeiten</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSave} className="space-y-4">
                <div className="bg-muted/50 rounded p-3">
                  <div className="text-sm">
                    <span className="font-medium">Typ: </span>
                    <span className="text-muted-foreground">
                      {editingTemplate && getTypeLabel(editingTemplate.notificationType)}
                    </span>
                  </div>
                  <div className="text-sm mt-1">
                    <span className="font-medium">Kanal: </span>
                    <span className="text-muted-foreground">
                      {editingTemplate?.channel === "email" ? "Email" : "SMS"}
                    </span>
                  </div>
                </div>

                {editingTemplate?.channel === "email" && (
                  <div>
                    <Label htmlFor="subject">Betreff</Label>
                    <Input
                      id="subject"
                      name="subject"
                      defaultValue={editingTemplate?.subject || ""}
                      placeholder="z.B. Buchungsbestätigung: {{courseName}}"
                      onChange={(e) => setPreviewSubject(e.target.value)}
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="template">Nachrichtenvorlage</Label>
                  <Textarea
                    id="template"
                    name="template"
                    defaultValue={editingTemplate?.template}
                    rows={10}
                    placeholder="Verwenden Sie {{variableName}} für dynamische Inhalte"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Verfügbare Variablen: participantName, courseName, courseDate, courseTime, courseLocation (mit doppelten geschweiften Klammern)
                  </p>
                </div>
                
                {/* Live Preview */}
                {previewTemplate && (
                  <div className="border rounded-md overflow-hidden">
                    <div className="bg-muted px-3 py-2 text-xs font-medium border-b flex items-center justify-between">
                      <span>Vorschau mit Beispieldaten</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setPreviewTemplate("")}
                      >
                        Schließen
                      </Button>
                    </div>
                    <div className="p-4 bg-white max-h-96 overflow-y-auto">
                      {previewSubject && (
                        <div className="mb-3 pb-3 border-b">
                          <span className="text-xs font-medium text-muted-foreground">Betreff:</span>
                          <div className="text-sm font-medium mt-1">{renderPreview(previewSubject)}</div>
                        </div>
                      )}
                      {previewTemplate.includes("<html") ? (
                        <iframe
                          srcDoc={renderPreview(previewTemplate)}
                          className="w-full min-h-[300px] border-0"
                          sandbox="allow-same-origin"
                          title="Template Preview"
                        />
                      ) : (
                        <div className="text-sm whitespace-pre-wrap">{renderPreview(previewTemplate)}</div>
                      )}
                    </div>
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const form = document.querySelector('form') as HTMLFormElement;
                    const formData = new FormData(form);
                    setPreviewTemplate(formData.get('template') as string || '');
                    setPreviewSubject(formData.get('subject') as string || '');
                  }}
                >
                  Vorschau anzeigen
                </Button>

                <div>
                  <Label htmlFor="description">Beschreibung (optional)</Label>
                  <Textarea
                    id="description"
                    name="description"
                    defaultValue={editingTemplate?.description || ""}
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsDialogOpen(false);
                      setPreviewTemplate("");
                      setPreviewSubject("");
                    }}
                  >
                    Abbrechen
                  </Button>
                  <Button type="submit">Speichern</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
